import { LogInterface } from '@evorait/pace-logging';
import { ODataObject } from '../../lib/interfaces/odata-model.interface';

export class TextInputUtilities {
    /**
     * Take an array of longtext items and extract the longtext contained within.
     * An optional joinToken is used to join the lines.
     *
     * @param lines
     * @param joinToken
     * @param log
     */
    public static getLongtext(lines: ODataLTextModel[], joinToken = ' ', log: LogInterface): string {
        const strings = this.getLongArray(lines, log).map((line) => line.TextLine);
        return strings.join(joinToken);
    }

    /**
     * Processes an array of text lines, handling [url] and [/url] tags that may
     * span across multiple lines, and returns a new array with formatted URLs.
     *
     * @param lines - Raw long text lines
     * @param log - Logger instance
     */
    public static getLongArray(lines: ODataLTextModel[], log: LogInterface): ODataLTextModel[] {
        const result: ODataLTextModel[] = [];
        let urlBuffer = '';
        let counter = 1;
        const urlStartTag = '[url]';
        const urlEndTag = '[/url]';

        // Sort lines by counter to preserve logical order
        const sortedLines = [...lines].sort((a, b) => a.Counter - b.Counter);

        // Finalizes the current URL when [/url] is found.
        // Adds the formatted link and any trailing text.
        const finalizeUrl = (textSegment: string, line: ODataLTextModel): void => {
            const [urlPart, trailingText = ''] = textSegment.split(urlEndTag);
            urlBuffer += urlPart;

            const formattedUrl = `<a href="${urlBuffer}" class="pace-link" target="_blank">${urlBuffer}</a>`;
            result.push({ ...line, TextLine: formattedUrl, Counter: counter++ });
            urlBuffer = '';

            if (trailingText.trim()) {
                result.push({ ...line, TextLine: trailingText, Counter: counter++ });
            }
        };

        // Handles lines containing [url], which may or may not close in the same line.
        const processUrlLine = (line: ODataLTextModel): void => {
            const parts = line.TextLine.split(urlStartTag);

            parts.forEach((segment, index) => {
                if (segment.includes(urlEndTag)) {
                    finalizeUrl(segment, line);
                } else if (index === 0 && !urlBuffer) {
                    if (segment.trim()) {
                        result.push({ ...line, TextLine: segment, Counter: counter++ });
                    }
                } else {
                    urlBuffer += segment;
                }
            });
        };

        for (const [index, line] of sortedLines.entries()) {
            const text = line.TextLine;
            if (text === null) {
                log.error(`TextInputUtilities: getLongArray: error: ${JSON.stringify(line)}`);
                continue;
            }

            if (text.includes(urlStartTag)) {
                processUrlLine(line);
            } else if (text.includes(urlEndTag) || (index === sortedLines.length - 1 && urlBuffer.length > 0)) {
                finalizeUrl(text, line);
            } else if (urlBuffer.length === 0) {
                result.push({ ...line, Counter: counter++ });
            } else {
                urlBuffer += text;
            }
        }
        return result;
    }
}

export interface ODataLTextModel extends ODataObject {
    __metadata: {
        id: string;
        uri: string;
        type: string;
    };
    NotificationNo: string;
    TaskNumber: string;
    TextID: string;
    TextLine: string;
    Counter: number;
}
